import * as R from 'ramda'

export const maxAnnualAllowanceOptions = [
  { label: '$5,000', value: 5000 },
  { label: '$10,000', value: 10000 },
  { label: '$15,000', value: 15000 },
  { label: '$20,000', value: 20000 },
  { label: '$25,000', value: 25000 },
  { label: '$30,000', value: 30000 },
  { label: '$35,000', value: 35000 },
  { label: '$40,000', value: 40000 },
  { label: '$45,000', value: 45000 },
  { label: '$50,000', value: 50000 },
  { label: '$2,000,000', value: 2000000 },
]

export const maxAnnualAllowanceOptionsWithYear = [
  { label: '$5,000/year', value: 5000 },
  { label: '$10,000/year', value: 10000 },
  { label: '$15,000/year', value: 15000 },
  { label: '$20,000/year', value: 20000 },
  { label: '$25,000/year', value: 25000 },
  { label: '$30,000/year', value: 30000 },
  { label: '$35,000/year', value: 35000 },
  { label: '$40,000/year', value: 40000 },
  { label: '$45,000/year', value: 45000 },
  { label: '$50,000/year', value: 50000 },
  { label: '$2,000,000/year', value: 2000000 },
]

export const deductibleOptions = [
  { label: '$0', value: 0 },
  { label: '$100', value: 100 },
  { label: '$200', value: 200 },
  { label: '$300', value: 300 },
  { label: '$400', value: 400 },
  { label: '$500', value: 500 },
  { label: '$600', value: 600 },
  { label: '$700', value: 700 },
  { label: '$800', value: 800 },
  { label: '$900', value: 900 },
  { label: '$1,000', value: 1000 },
]

/**
 * Determines if the given deductible is currently considered a catastrophic
 * rider. This is done to keep the business logic in a centralized location in
 * case we change more catastrophic riders in the future.
 *
 * @param {number} deductible
 *
 *   | policy deductible
 * @returns {boolean}
 */
export const isCatastrophicRider = deductible => {
  switch (deductible) {
    case 10000:
    case 20000:
      return true
    default:
      return false
  }
}

/**
 * Determines the available catastrophic riders available to the user. It takes
 * into account if the user already has an existing deductible that may be
 * deprecated
 *
 * @param {string} insuranceFilingUuid The insuranceFilingUuid of the most
 *   current quote
 * @param {number} currentDeductible The current deductible to include in the
 *   options
 * @returns {Array}
 */
export const getCatastrophicRiders = (
  insuranceFilingUuid,
  currentDeductible,
) => {
  const catastrophicRiders = []

  const newDeductible = { label: '$10,000', value: 10000 }
  const deprecatedDeductible = { label: '$20,000', value: 20000 }

  const isPreviousPolicy = ['sch_2018_2019_2020', 'sch_non_tx_2018'].includes(
    insuranceFilingUuid,
  )
  // this ensures that you're going to have the correct catastrophic deductible
  // depending on your insuranceFilingUuid, as well as your current deductible
  // available in the options, and in the correct order
  if (isPreviousPolicy) {
    if (currentDeductible === newDeductible.value) {
      catastrophicRiders.push(newDeductible)
    }
    catastrophicRiders.push(deprecatedDeductible)
  } else {
    catastrophicRiders.push(newDeductible)
    if (currentDeductible === deprecatedDeductible.value) {
      catastrophicRiders.push(deprecatedDeductible)
    }
  }

  return catastrophicRiders
}

/**
 * Determines the maternityAllowance options based on insuranceFilingUuid
 *
 * @param {string} insuranceFilingUuid The insuranceFilingUuid of the most
 *   current quote
 * @param {number} currentMaternityAllowance The current maternityAllowance to
 *   include in the options
 * @returns {Array}
 */
export const getMaternityAllowanceOptions = (
  insuranceFilingUuid,
  currentMaternityAllowance,
) => {
  const isPreviousPolicy = ['sch_2018_2019_2020', 'sch_non_tx_2018'].includes(
    insuranceFilingUuid,
  )

  // These options are now only available for previous insuranceFilingUuid
  // or if you already have an option
  const deprecatedOptions = [
    { label: '$100,000', value: 100000 },
    { label: '$200,000', value: 200000 },
    { label: '$300,000', value: 300000 },
    { label: '$400,000', value: 400000 },
    { label: '$500,000', value: 500000 },
    { label: '$1,000,000', value: 1000000 },
  ]

  return [
    { label: '$10,000', value: 10000 },
    { label: '$15,000', value: 15000 },
    { label: '$20,000', value: 20000 },
    { label: '$25,000', value: 25000 },
    { label: '$30,000', value: 30000 },
    { label: '$35,000', value: 35000 },
    { label: '$40,000', value: 40000 },
    { label: '$45,000', value: 45000 },
    { label: '$50,000', value: 50000 },
    ...deprecatedOptions.filter(
      ({ value }) => value === currentMaternityAllowance || isPreviousPolicy,
    ),
    { label: '$2,000,000', value: 2000000 },
  ]
}

/** Formats the maternity allowance options with year for design purposes */
export const getMaternityAllowanceOptionsWithYear = R.pipe(
  getMaternityAllowanceOptions,
  R.map(R.evolve({ label: label => `${label}/year` })),
)

export const maternityDeductibleOptions = [
  { label: '$0', value: 0 },
  { label: '$1,000', value: 1000 },
  { label: '$2,000', value: 2000 },
  { label: '$3,000', value: 3000 },
  { label: '$4,000', value: 4000 },
  { label: '$5,000', value: 5000 },
]
